import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { UseDimensionConfigPayload } from 'libs/end-to-end/src/use-dimension-config-payload';
import { getInsightFormatOrDefault } from 'libs/end-to-end/src/insights';
import { getInsightWithReferenceFormatOrDefault } from 'libs/end-to-end/src/insights';
import { Insight } from 'libs/end-to-end/src/insights';
import { UppercaseFirst } from 'apps/sigma2-ui/src/lib/translations/translation-utils';
import upperFirst from 'lodash/upperFirst';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import cn from 'classnames';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TREATMENT_RECOMMENDATIONS } from 'apps/sigma2-ui/src/constants/data';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { InsightImage } from 'apps/sigma2-ui/src/components/insights/InsightImage';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';

export interface InsightsCardProps extends Insight {
  widthClassName?: string;
  focusArea: MyFocusAreaDefinition;
  insightImageStyle?: 'hidden' | 'visible-top' | 'visible-right';
  dimensionsPayload: UseDimensionConfigPayload;
  calibrationFraming: CalibrationFraming;
}

const GrayDot: React.FC = () => {
  return <span className="w-1 h-1 rounded-full bg-gray-600" />;
};

export const InsightsCard: React.FC<InsightsCardProps> = ({
  widthClassName,
  focusArea,
  insightImageStyle = 'visible-top',
  calibrationFraming,
  dimensionsPayload,
  ...insight
}) => {
  const [searchParams, setUrlSearchParams] = useSearchParams();
  const language = useLanguage();
  const { getLevelByColumnValue, getDimensionByColumnHeaderValue } =
    dimensionsPayload;

  const { recommendations } = insight;

  const { title, summary, levelDisplayRaw } = useMemo(() => {
    const dimensionDefinition = getDimensionByColumnHeaderValue(
      insight.dimension,
    );

    if (!dimensionDefinition) {
      return {
        title: '',
        summary: '',
        levelDisplayRaw: '',
      };
    }

    const focusAreaDisplay = focusArea.displayName.toLocaleLowerCase();

    const levelDisplayRaw =
      getLevelByColumnValue(insight.dimension, insight.treatmentLevel)
        ?.displayName ?? '';

    const levelDisplay = levelDisplayRaw ?? '';

    const referenceDisplay =
      getLevelByColumnValue(
        insight.dimension,
        insight.referenceLevel,
      )?.displayName.toLocaleLowerCase() ?? '';

    const metricDefinition = focusArea.metrics.find(
      (metric) => metric.columnHeaderValue === insight.metric,
    );

    const subjectDisplay = metricDefinition?.whiteLabeling.subjectPlural ?? '';

    const insightFormat = getInsightFormatOrDefault(
      dimensionDefinition.insightFormat,
    );
    const insightWithReferenceFormat = getInsightWithReferenceFormatOrDefault(
      dimensionDefinition.insightWithReferenceFormat,
    );
    const showBeforeEmpsOrApps =
      dimensionDefinition.showBeforeEmpsOrApps ?? false;

    const insightDisplay = getTranslatedContentAsString(
      language.insightDisplay({
        insightFormat,
        levelDisplay,
        empsOrAppsDisplay: subjectDisplay,
        showBeforeEmpsOrApps,
      }),
    ).toLocaleLowerCase();

    const insightWithReferenceDisplay = getTranslatedContentAsString(
      language.insightWithReferenceDisplay({
        insightWithReferenceFormat,
        levelDisplay,
        empsOrAppsDisplay: subjectDisplay,
        showBeforeEmpsOrApps,
        referenceDisplay,
      }),
    ).toLocaleLowerCase();

    const summary = convertTranslatedContentToReactNode(
      language.insightTextNew({
        insight,
        phrases: language,
        focusAreaDisplay,
        insightDisplay,
        insightWithReferenceDisplay,
        lowerHigherDisplay: getTranslatedContentAsString(
          language.lowerHigherDisplay({
            type: insight?.highlight?.type ?? '',
          }),
        ),
        calibrationFraming,
      }),
    );

    const title = upperFirst(
      language.insightTitleNew({
        insight,
        focusAreaDisplay,
        insightDisplay,
        insightWithReferenceDisplay,
        phrases: language,
        calibrationFraming,
      }),
    );

    return {
      summary,
      title,
      levelDisplayRaw,
    };
  }, [
    getDimensionByColumnHeaderValue,
    insight,
    focusArea.displayName,
    focusArea.metrics,
    getLevelByColumnValue,
    language,
    calibrationFraming,
  ]);

  return (
    <Spacing
      direction="horizontal"
      size="md"
      className="items-start justify-between"
    >
      <Spacing
        direction="vertical"
        size="sm"
        className={cn(widthClassName ?? 'max-w-md')}
      >
        {insightImageStyle === 'visible-top' && (
          <div className="pb-2">
            <InsightImage
              insight={insight}
              calibrationFraming={calibrationFraming}
            />
          </div>
        )}

        {recommendations?.[0] && (
          <Spacing direction="horizontal" size="sm" className="items-center">
            <S2Typography
              variant="headline-7-alt"
              translatedContent={language[
                TREATMENT_RECOMMENDATIONS[recommendations[0]].messageId
              ]()}
            />
          </Spacing>
        )}

        {!recommendations?.[0] && (
          <S2Typography
            variant="headline-7-alt"
            translatedContent={language.insightType({
              insight,
              levelDisplay: levelDisplayRaw,
            })}
          />
        )}

        <button
          onClick={() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('type', insight.type);
            newParams.set('dimension', insight.dimension);
            newParams.set('treatmentLevel', insight.treatmentLevel);

            setUrlSearchParams(newParams);
          }}
          className="hover:underline hover:underline-offset-4"
        >
          <Heading level={3} textColor="text-gray-1000" className="text-left">
            {title}
          </Heading>
        </button>

        {!!summary && (
          <UppercaseFirst>
            <Paragraph textColor="text-gray-800">{summary}</Paragraph>
          </UppercaseFirst>
        )}

        <Spacing direction="horizontal" size="md" className="items-center">
          <S2Typography
            variant="body-7"
            translatedContent={language.insightNumMinutesToRead({ insight })}
          />

          {insight.confidencePercentage && <GrayDot />}

          {insight.confidencePercentage && (
            <S2Typography
              variant="body-7"
              translatedContent={language.insightConfidencePercentage({
                insight,
              })}
            />
          )}
        </Spacing>
      </Spacing>

      {insightImageStyle === 'visible-right' && (
        <div className="w-[15rem]">
          <InsightImage
            insight={insight}
            calibrationFraming={calibrationFraming}
          />
        </div>
      )}
    </Spacing>
  );
};
